import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios'; // Needed for API calls

// Create Context
const AuthContext = createContext(null);

// Create Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // Store user data
  const [token, setToken] = useState(localStorage.getItem('authToken')); // Get token from local storage initially
  const [loading, setLoading] = useState(true); // Loading state for initial auth check

  // Effect to check token validity and fetch user profile on initial load or token change
  useEffect(() => {
    const verifyTokenAndFetchUser = async () => {
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        try {
          // Fetch user profile using the token
          const response = await axios.get('/users/me'); // Removed prefix
          setUser(response.data.data.user); // Store full user object
        } catch (error) {
          // Handle errors (e.g., token expired, invalid)
          console.error('Token verification failed:', error.response?.data || error.message);
          localStorage.removeItem('authToken'); // Clear invalid token
          setToken(null);
          setUser(null);
          delete axios.defaults.headers.common['Authorization'];
        }
      } else {
        // No token found
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
      }
      setLoading(false); // Finished initial check/fetch
    };

    verifyTokenAndFetchUser();
  }, [token]); // Rerun when token changes

  // Login function
  const login = async (username, password) => {
    try {
      setLoading(true);
      // Use relative path now that baseURL is fixed
      console.log('Attempting login with baseURL:', axios.defaults.baseURL);
      const response = await axios.post('/users/login', { username, password }); // Changed from email to username
      const { token: newToken, data: { user: userData } } = response.data;

      localStorage.setItem('authToken', newToken); // Store token in local storage
      setToken(newToken); // Update state
      setUser(userData); // Store user data
      setLoading(false);
      return { success: true };
    } catch (error) {
      console.error('Login failed:', error.response?.data || error.message);
      localStorage.removeItem('authToken'); // Clear token on failure
      setToken(null);
      setUser(null);
      setLoading(false);
      return { success: false, message: error.response?.data?.message || 'Login failed' };
    }
  };

  // Logout function
  const logout = () => {
    setLoading(true);
    localStorage.removeItem('authToken'); // Remove token from local storage
    setToken(null); // Clear state
    setUser(null);
    setLoading(false);
    // No need to redirect here, App.js routing handles it based on user state
  };

  // Value provided to consuming components
  const value = {
    user,
    token,
    isAuthenticated: !!user, // Simple check if user object exists
    loading,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {/* Don't render children until initial loading is complete */}
      {!loading && children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the AuthContext
export const useAuth = () => {
  return useContext(AuthContext);
};
